/**
 * Chart Utilities
 * 
 * Utility functions for transforming data and configuring charts.
 */

import { TrendChartData, TechnologyType } from '../../../services/equipment/types';

/**
 * Gets display name for technology type
 */
export const getTechnologyDisplayName = (technology: TechnologyType): string => {
  const technologyMap: Record<TechnologyType, string> = {
    monitron: 'Monitron (Vibration and Temperature)',
    pulse: 'Trend',
    cadet: 'CADET (Current)',
    thermal: 'ASTRA (Thermal)',
  };
  
  return technologyMap[technology];
};

/**
 * Gets appropriate units for each technology type
 */
export const getTechnologyUnit = (technology: TechnologyType): string => {
  const unitMap: Record<TechnologyType, string> = {
    monitron: 'mm/s',
    pulse: 'dB',
    cadet: 'Hz',
    thermal: '°C',
  };
  
  return unitMap[technology];
};

/**
 * Formats timestamp for axis labels
 */
export const formatTimestampForAxis = (timestamp: Date): string => {
  return timestamp.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats value for axis labels
 */
export const formatValueForAxis = (value: number, index: number): string => {
  // Don't show label for first tick to avoid crowding
  return index > 0 ? value.toString() : '';
};

/**
 * Formats tooltip labels
 */
export const formatTooltipLabel = ({ datum }: { datum: any }): string => {
  return `${datum.y}`;
};

/**
 * Gets color scheme for technology type
 */
export const getTechnologyColors = (technology: TechnologyType): { nominal: string; baseline: string } => {
  const colorMap: Record<TechnologyType, { nominal: string; baseline: string }> = {
    monitron: { nominal: '#0073bb', baseline: '#6c757d' },
    pulse: { nominal: '#28a745', baseline: '#6c757d' },
    cadet: { nominal: '#ffc107', baseline: '#6c757d' },
    thermal: { nominal: '#fd7e14', baseline: '#6c757d' },
  };
  
  return colorMap[technology];
};

/**
 * Calculates responsive chart dimensions based on container width
 */
export const getResponsiveChartDimensions = (containerWidth: number): { width: number; height: number } => {
  if (containerWidth < 600) {
    // Mobile
    return { width: containerWidth - 32, height: 200 };
  } else if (containerWidth < 1024) {
    // Tablet
    return { width: Math.min(containerWidth - 64, 500), height: 240 };
  } else {
    // Desktop
    return { width: 600, height: 260 };
  }
};

/**
 * Checks if chart data is empty or invalid
 */
export const isChartDataEmpty = (trendData: TrendChartData): boolean => {
  return !trendData.timeSeries.length;
};

/**
 * Gets chart configuration for specific technology
 */
export const getTechnologyChartConfig = (technology: TechnologyType) => {
  const colors = getTechnologyColors(technology);
  const unit = getTechnologyUnit(technology);
  const displayName = getTechnologyDisplayName(technology);

  return {
    colors,
    unit,
    displayName,
    showIndependentGrid: technology === 'thermal', // Show grid for thermal charts
    showCrosshair: true,
  };
};